import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { toast } from '@/Components/ui/use-toast';
import { Link } from '@inertiajs/react';
import { ArrowRight, Leaf, Loader2, Mail, Sprout } from 'lucide-react';
import React, { useState } from 'react';
import { useSubscribe } from './useSubscribe';

export type CTAType =
    | 'subscribe'
    | 'contact'
    | 'register'
    | 'apply'
    | 'demo'
    | 'learn-more'
    | 'testimonial';
export type BackgroundType = 'green' | 'white' | 'gradient' | 'image';

interface CTAProps {
    type: CTAType;
    title?: string;
    description?: string;
    buttonText?: string;
    className?: string;
    href?: string;
    onAction?: (data?: any) => void;
    background?: BackgroundType;
    backgroundImage?: string;
    overlayColor?: string;
}

const CTA: React.FC<CTAProps> = ({
    type,
    title,
    description,
    buttonText,
    className = '',
    href,
    onAction,
    background = 'gradient',
    backgroundImage,
    overlayColor = 'bg-emerald-900/40',
}) => {
    const [email, setEmail] = useState('');
    const [isHovered, setIsHovered] = useState(false);
    const { subscribe, isLoading } = useSubscribe();

    const backgroundStyles = {
        green: 'bg-gradient-to-br from-emerald-600 to-emerald-700',
        white: 'bg-white dark:bg-gray-800',
        gradient:
            'bg-gradient-to-br from-emerald-50 via-amber-50 to-emerald-100 dark:from-emerald-900/50 dark:via-amber-900/50 dark:to-emerald-800/50',
        image: 'relative bg-cover bg-center bg-no-repeat',
    };

    const containerClasses = `
        relative overflow-hidden
        py-16 sm:py-20
        ${backgroundStyles[background]}
        ${className}
        transition-all duration-500 ease-in-out
    `;

    const handleSubscribe = async (e: React.FormEvent) => {
        e.preventDefault();
        if (email && /\S+@\S+\.\S+/.test(email)) {
            try {
                await subscribe(email);
                setEmail('');
                onAction?.(email);
                toast({
                    title: 'Welcome to Gaphen!',
                    description:
                        'Thank you for joining our mission to transform agriculture.',
                });
            } catch (error) {
                // Error is handled in the hook
            }
        } else {
            toast({
                description: 'Please enter a valid email address',
                variant: 'destructive',
            });
        }
    };

    const renderBackground = () => {
        if (background === 'image' && backgroundImage) {
            return (
                <>
                    <div
                        className="absolute inset-0 scale-105 transform bg-cover bg-center bg-no-repeat transition-transform duration-700"
                        style={{
                            backgroundImage: `url(${backgroundImage})`,
                            transform: isHovered ? 'scale(1.1)' : 'scale(1.05)',
                        }}
                    />
                    <div
                        className={`absolute inset-0 ${overlayColor} transition-opacity duration-700`}
                    />
                </>
            );
        }
        return null;
    };

    const renderContent = () => {
        const textColorClass =
            background === 'white'
                ? 'text-gray-900 dark:text-gray-100'
                : 'text-white';

        const decorativeElements = (
            <>
                <Leaf className="absolute -left-6 top-1/4 h-12 w-12 -rotate-45 transform text-emerald-500/20" />
                <Sprout className="absolute -right-6 bottom-1/4 h-12 w-12 rotate-45 transform text-emerald-500/20" />
            </>
        );

        switch (type) {
            case 'subscribe':
                return (
                    <div className="relative mx-auto max-w-3xl text-center">
                        {decorativeElements}
                        <h2
                            className={`mb-4 text-4xl font-bold tracking-tight ${textColorClass} transition-transform duration-300 hover:scale-105`}
                        >
                            {title}
                        </h2>
                        <p
                            className={`mb-8 text-lg ${textColorClass} opacity-90`}
                        >
                            {description}
                        </p>
                        <form
                            onSubmit={handleSubscribe}
                            className="flex flex-col items-center justify-center gap-4 sm:flex-row"
                            onMouseEnter={() => setIsHovered(true)}
                            onMouseLeave={() => setIsHovered(false)}
                        >
                            <Input
                                type="email"
                                placeholder="Enter your email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="flex-grow border-white/30 bg-white/20 text-white backdrop-blur-sm placeholder:text-white/70"
                                disabled={isLoading}
                            />
                            <Button
                                type="submit"
                                disabled={isLoading}
                                className="w-full bg-white text-emerald-600 transition-all duration-300 hover:bg-emerald-50 dark:bg-emerald-600 dark:text-white dark:hover:bg-emerald-700 sm:w-auto"
                            >
                                {isLoading ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                    <Mail className="mr-2 h-4 w-4" />
                                )}
                                Subscribe
                            </Button>
                        </form>
                    </div>
                );

            default:
                return (
                    <div className="relative mx-auto max-w-3xl text-center">
                        {decorativeElements}
                        <h2
                            className={`mb-4 text-4xl font-bold tracking-tight ${textColorClass}`}
                        >
                            {title}
                        </h2>
                        <p
                            className={`mb-8 text-lg ${textColorClass} opacity-90`}
                        >
                            {description}
                        </p>
                        {buttonText && (
                            <Button
                                asChild
                                className="bg-white text-emerald-600 transition-all duration-300 hover:bg-emerald-50 dark:bg-emerald-600 dark:text-white dark:hover:bg-emerald-700"
                            >
                                <Link
                                    href={href || '#'}
                                    className="inline-flex items-center"
                                    onMouseEnter={() => setIsHovered(true)}
                                    onMouseLeave={() => setIsHovered(false)}
                                >
                                    {buttonText}
                                    <ArrowRight
                                        className={`ml-2 h-4 w-4 transition-transform duration-300 ${isHovered ? 'translate-x-1' : ''}`}
                                    />
                                </Link>
                            </Button>
                        )}
                    </div>
                );
        }
    };

    return (
        <section
            className={containerClasses}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div className="container mx-auto px-4">
                {background === 'image' ? (
                    <div className="relative">
                        {renderBackground()}
                        <div className="relative z-10">{renderContent()}</div>
                    </div>
                ) : (
                    renderContent()
                )}
            </div>
        </section>
    );
};

export default CTA;
